import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AllCountriesSelect } from '@/features/installations/components/all-countries-select'
import { OrganizationAddressFormColumn } from '@/features/organization-settings/components/organization-address-form-column'
import type { OrganizationCompanyFormState } from '@/features/organization-settings/organization-company-form-state'

type OrganizationAddressSectionCardProps = {
  state: OrganizationCompanyFormState
  countryName: string
  doesOrganizationRequiresLocalAddress: boolean
  latinErrorMessage: string
  onSingleCountryAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnEnglishAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnLocalAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  showRequiredIndicators?: boolean
  countryEditable?: boolean
  variant?: 'card' | 'plain'
  onCountryChange?: (isoAlpha2UpperCase: string) => void
  countryPlaceholder?: string
}

export function OrganizationAddressSectionCard({
  state,
  countryName,
  doesOrganizationRequiresLocalAddress,
  latinErrorMessage,
  onSingleCountryAddressInput,
  onCnEnglishAddressInput,
  onCnLocalAddressInput,
  showRequiredIndicators = true,
  countryEditable = false,
  variant = 'card',
  onCountryChange,
  countryPlaceholder,
}: OrganizationAddressSectionCardProps) {
  const { t } = useTranslation(['settings', 'common'])

  const countryField = (
    <div className="max-w-md">
      <Label htmlFor="organization-address-country">
        {t('common:inputFields.country.label')}
      </Label>
      {countryEditable && onCountryChange ? (
        <AllCountriesSelect
          id="organization-address-country"
          value={state.country}
          onValueChange={onCountryChange}
          placeholder={
            countryPlaceholder ?? t('common:inputFields.country.label')
          }
          triggerClassName="mt-2"
        />
      ) : (
        <Input value={countryName} disabled className="mt-2" />
      )}
    </div>
  )

  const fields = doesOrganizationRequiresLocalAddress ? (
    <div className="space-y-4">
      <Alert>
        <AlertDescription>
          {t('settings:organizationDetails.details.localAddressInfo')}
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 lg:grid-cols-2">
        <OrganizationAddressFormColumn
          isLocal
          countryCode={state.country}
          street={state.localStreet}
          streetNumber={state.localStreetNumber}
          street2={state.localStreet2}
          postCode={state.localPostCode}
          poBox={state.localPoBox}
          city={state.localCity}
          subDivision={state.localSubDivision}
          latinErrorMessage={latinErrorMessage}
          onChange={onCnLocalAddressInput}
          inputIdPrefix="local-"
          showRequiredIndicators={showRequiredIndicators}
        />
        <OrganizationAddressFormColumn
          isLocal={false}
          countryCode={state.country}
          street={state.street}
          streetNumber={state.streetNumber}
          street2={state.street2}
          postCode={state.postCode}
          poBox={state.poBox}
          city={state.city}
          subDivision={state.subDivision}
          latinErrorMessage={latinErrorMessage}
          onChange={onCnEnglishAddressInput}
          showRequiredIndicators={showRequiredIndicators}
        />
      </div>
      {countryField}
    </div>
  ) : (
    <div className="space-y-4">
      <OrganizationAddressFormColumn
        isLocal={false}
        countryCode={state.country}
        street={state.street}
        streetNumber={state.streetNumber}
        street2={state.street2}
        postCode={state.postCode}
        poBox={state.poBox}
        city={state.city}
        subDivision={state.subDivision}
        latinErrorMessage={latinErrorMessage}
        onChange={onSingleCountryAddressInput}
        showRequiredIndicators={showRequiredIndicators}
      />
      {countryField}
    </div>
  )

  if (variant === 'plain') {
    return fields
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MapPin className="size-5 shrink-0" />
          <CardTitle>{t('settings:organizationDetails.details.address')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>{fields}</CardContent>
    </Card>
  )
}
